<template>
    <div>
        <h3>Understanding your tyres</h3>
        <p>Tyre size can be found on the sidewall of a tyre and appears as a combination of numbers and letters. Knowing what each section of these numbers and letters mean will make it very easy to find your car's tyre size. The below image shows a tyre with a mixture of numbers and letters: <b>205 55 R16 91W</b>.</p>
        <img class="my-2" src="/assets/img/helpAdvice/width_profile.jpeg" alt="Width Profile Diameter Speed" />
        <p class="my-2">To explain the tyre size further we have broken each section down, explaining what each number and letter means.</p>
        <table align="center" class="table table-bordered terms-table table-responsive table-striped tyre-help-table" width="100%">
            <tr>
                <th width="20%"><p><b>Sidewall Marking</b></p></th>
                <th width="80%"><p><b>Explanation</b></p></th>
            </tr>
            <tr>
                <td><p>205</p></td>
                <td>The width of the tyre in millimetres</td>
            </tr>
            <tr>
                <td><p>55</p></td>
                <td>Height of the tyre sidewall as a percentage of the width</td>
            </tr>
            <tr>
                <td><p>R</p></td>
                <td>The tyre is of radial construction</td>
            </tr>
            <tr>
                <td><p>16</p></td>
                <td>The tyre's inner rim diameter in inches</td>
            </tr>
            <tr>
                <td><p>91</p></td>
                <td>The load rating of the tyre</td>
            </tr>
            <tr>
                <td><p>W</p></td>
                <td>This speed rating - the tyres maximum speed at full load</td>
            </tr>
        </table>
        <h3 class="my-2">Tyre load rating</h3>
        <p>The tyre load rating indicates the maximum weight each tyre is able to carry.</p>
        <p>If a tyre with an incorrect load capacity is fitted to your car your insurance policy can become void in certain cases. It is therefore vital to check your car is fitted with a tyre with the correct load rating.</p>
        <p>The load rating of a tyre is usually found after the diameter reading and before the speed rating on the sidewall marking.</p>
        <p>The table below shows the weight capacity in kilograms of each load rating.</p>
        <table align="center" class="table table-bordered table-responsive table-striped tyre-help-table tyre-stats-table" width="100%">
            <tr>
                <th><p><b>Load index</b></p></th>
                <th><p><b>Load in kg</b></p></th>
                <th><p><b>Load index</b></p></th>
                <th><p><b>Load in kg</b></p></th>
                <th><p><b>Load index</b></p></th>
                <th><p><b >Load in kg</b></p></th>
            </tr>
            <tr>
                <td><p>62</p></td>
                <td><p>265</p></td>
                <td><p>84</p></td>
                <td><p>500</p></td>
                <td><p>106</p></td>
                <td><p>950</p></td>
            </tr>
            <tr>
                <td><p>63</p></td>
                <td><p>272</p></td>
                <td><p>85</p></td>
                <td><p>515</p></td>
                <td><p>107</p></td>
                <td><p>975</p></td>
            </tr>
            <tr>
                <td><p>64</p></td>
                <td><p>280</p></td>
                <td><p>86</p></td>
                <td><p>530</p></td>
                <td><p>108</p></td>
                <td><p>1000</p></td>
            </tr>
            <tr>
                <td><p>65</p></td>
                <td><p>290</p></td>
                <td><p>87</p></td>
                <td><p>545</p></td>
                <td><p>109</p></td>
                <td><p>1030</p></td>
            </tr>
            <tr>
                <td><p>66</p></td>
                <td><p>300</p></td>
                <td><p>88</p></td>
                <td><p>560</p></td>
                <td><p>110</p></td>
                <td><p>1060</p></td>
            </tr>
            <tr>
                <td><p>67</p></td>
                <td><p>307</p></td>
                <td><p>89</p></td>
                <td><p>580</p></td>
                <td><p >111</p></td>
                <td><p>1090</p></td>
            </tr>
            <tr>
                <td><p>68</p></td>
                <td><p>315</p></td>
                <td><p>90</p></td>
                <td><p>600</p></td>
                <td><p>112</p></td>
                <td><p>1120</p></td>
            </tr>
            <tr>
                <td><p>69</p></td>
                <td><p>325</p></td>
                <td><p>91</p></td>
                <td><p>615</p></td>
                <td><p>113</p></td>
                <td><p>1150</p></td>
            </tr>
            <tr>
                <td><p>70</p></td>
                <td><p>335</p></td>
                <td><p>92</p></td>
                <td><p>630</p></td>
                <td><p>114</p></td>
                <td><p>1180</p></td>
            </tr>
            <tr>
                <td><p>71</p></td>
                <td><p>345</p></td>
                <td><p>93</p></td>
                <td><p>650</p></td>
                <td><p>115</p></td>
                <td><p>1215</p></td>
            </tr>
            <tr>
                <td><p>72</p></td>
                <td><p>355</p></td>
                <td><p>94</p></td>
                <td><p>670</p></td>
                <td><p>116</p></td>
                <td><p>1250</p></td>
            </tr>
            <tr>
                <td><p>73</p></td>
                <td><p>365</p></td>
                <td><p>95</p></td>
                <td><p>690</p></td>
                <td><p>117</p></td>
                <td><p>1285</p></td>
            </tr>
            <tr>
                <td><p>74</p></td>
                <td><p>375</p></td>
                <td><p>96</p></td>
                <td><p>710</p></td>
                <td><p>118</p></td>
                <td><p>1320</p></td>
            </tr>
            <tr>
                <td><p>75</p></td>
                <td><p>387</p></td>
                <td><p>97</p></td>
                <td><p>730</p></td>
                <td><p>119</p></td>
                <td><p>1360</p></td>
            </tr>
            <tr>
                <td><p>76</p></td>
                <td><p>400</p></td>
                <td><p>98</p></td>
                <td><p>750</p></td>
                <td><p >120</p></td>
                <td><p>1400</p></td>
            </tr>
            <tr>
                <td><p>77</p></td>
                <td><p>412</p></td>
                <td><p>99</p></td>
                <td><p>775</p></td>
                <td><p >121</p></td>
                <td><p>1450</p></td>
            </tr>
            <tr>
                <td><p>78</p></td>
                <td><p>425</p></td>
                <td><p>100</p></td>
                <td><p>800</p></td>
                <td><p >122</p></td>
                <td><p>1500</p></td>
            </tr>
            <tr>
                <td><p>79</p></td>
                <td><p>437</p></td>
                <td><p>101</p></td>
                <td><p>825</p></td>
                <td><p>123</p></td>
                <td ><p>1550</p></td>
            </tr>
            <tr>
                <td><p>80</p></td>
                <td><p>450</p></td>
                <td><p>102</p></td>
                <td><p>850</p></td>
                <td><p>124</p></td>
                <td><p>1600</p></td>
            </tr>
            <tr>
                <td><p>81</p></td>
                <td><p>462</p></td>
                <td><p>103</p></td>
                <td><p>875</p></td>
                <td><p>125</p></td>
                <td ><p>1650</p></td>
            </tr>
            <tr>
                <td><p>82</p></td>
                <td><p>475</p></td>
                <td><p>104</p></td>
                <td><p>900</p></td>
                <td><p>126</p></td>
                <td><p>1700</p></td>
            </tr>
            <tr>
                <td><p >83</p></td>
                <td><p>487</p></td>
                <td><p >105</p></td>
                <td><p>925</p></td>
                <td><p></p></td>
                <td><p></p></td>
            </tr>
        </table>
        <h3 class="my-2">Tyre speed rating</h3>
        <p>The speed rating of a tyre is indicated by a letter at the end of the tyre size on the sidewall of the tyre, and specifies the maximum speed your tyre is capable of maintaining.</p>
        <p>For example a tyre with a speed rating of "W" can reach speeds of up to 168mph.</p>
        <p>The load rating of a tyre is usually found after the diameter reading and before the speed rating on the sidewall marking.</p>
        <p>A tyre's speed rating is the result of specific tests ran by engineers. These involve running the tyre at 6.2mph steps in ten minute increments until the required speed has been met.</p>
        <p>If your car is fitted with a tyre with a lower speed rating than is appropriate for your vehicle you could invalidate your insurance policy, therefore it is essential you check your car's speed rating before purchasing new tyres.</p>
        <p>The table below shows the maximum speed in mph of each speed rating.</p>
        <table align="center" class="table table-bordered terms-table table-responsive table-striped tyre-help-table tyre-stats-table" width="100%">
            <tr>
                <th width="50%"><p><b>Sidewall Marking</b></p></th>
                <th width="50%"><p><b>Max speed (MPH)</b></p></th>
            </tr>
            <tr>
                <td><p>N</p></td>
                <td>87</td>
            </tr>
            <tr>
                <td><p>P</p></td>
                <td>93</td>
            </tr>
            <tr>
                <td><p>Q</p></td>
                <td>99</td>
            </tr>
            <tr>
                <td><p>R</p></td>
                <td>106</td>
            </tr>
            <tr>
                <td><p>S</p></td>
                <td>112</td>
            </tr>
            <tr>
                <td><p>T</p></td>
                <td>118</td>
            </tr>
            <tr>
                <td><p>U</p></td>
                <td>124</td>
            </tr>
            <tr>
                <td><p>H</p></td>
                <td>130</td>
            </tr>
            <tr>
                <td><p>V</p></td>
                <td>149</td>
            </tr>
            <tr>
                <td><p>Z</p></td>
                <td>150+</td>
            </tr>
            <tr>
                <td><p>W</p></td>
                <td >168</td>
            </tr>
            <tr>
                <td><p>Y</p></td>
                <td >186</td>
            </tr>
        </table>
    </div>
</template>

<script>
    export default {
        
    }
</script>

<style lang="scss" scoped>
    table {
        p {
            margin: 0;
        }
    }
</style>